import { useCallback, useEffect, useRef, useState } from "react";
import { useShellFocusResizer } from "@/hooks/use-shell-focus-resizer";
import { TERMINAL_OPEN_STORAGE_KEY } from "./lazy-imports";
import { isElectronRuntime } from "@/lib/logout";

/**
 * Owns the terminal-open / shell-focus-level / terminal-width tuple plus
 * the cross-component window events that drive them
 * (`terminal:open`, `ano:collapse-terminal`, `message:send-to-shell`).
 *
 * `showChat` and `showSidebar` are derived from the same state so callers
 * read a single source of truth.
 *
 * `handleCycleViewMode` is the Cmd+J 4-mode cycle:
 *   Mode 0: sidebar + chat (no terminal)
 *   Mode 1: sidebar + chat + terminal
 *   Mode 2: sidebar + terminal (no chat)
 *   Mode 3: terminal only
 */
export function useViewMode() {
  // The Shell is Electron-only. In a browser its IPC bridge is absent,
  // so a stale localStorage flag would otherwise mount an empty terminal
  // panel and the toggle keybinding would silently no-op. Force the
  // surface closed and the open-state flag false in web.
  const isShellAvailable = isElectronRuntime();
  const [terminalOpen, setTerminalOpen] = useState(() => {
    if (!isShellAvailable) return false;
    try {
      return localStorage.getItem(TERMINAL_OPEN_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });

  // Lazy-mount TerminalPanel: it subscribes to useChat + Zero queries on mount,
  // which run even when the panel is CSS-hidden. Only mount once the user has
  // opened the terminal at least once this session (or had it persisted open
  // from a previous session). Once mounted, stays mounted (CSS-hidden on close)
  // so xterm / useChat state survives future toggles.
  const [hasOpenedTerminal, setHasOpenedTerminal] = useState(() => {
    if (!isShellAvailable) return false;
    try {
      return localStorage.getItem(TERMINAL_OPEN_STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  if (terminalOpen && !hasOpenedTerminal) setHasOpenedTerminal(true);

  const [terminalWidth, setTerminalWidth] = useState(352);
  // Shell focus level: 0 = sidebar+chat+terminal, 1 = sidebar+terminal, 2 = terminal only
  // Not persisted — window bounds can't be restored across app restarts
  const [shellFocusLevel, setShellFocusLevel] = useState<0 | 1 | 2>(0);

  // Derived visibility flags
  const showChat = !terminalOpen || shellFocusLevel === 0;
  const showSidebar = !terminalOpen || shellFocusLevel <= 1;

  // Window bounds management for shell focus modes — extracted to
  // `useShellFocusResizer` so the geometry-capture + scale-aware shrink
  // logic stays out of the AppInner closure.
  const resizeWindowForFocusLevel = useShellFocusResizer(terminalWidth);

  const handleToggleTerminal = useCallback(async () => {
    // No-op in web: there's no Electron IPC bridge for the terminal,
    // so opening the panel would render an empty xterm and the toggle
    // accelerator (Cmd+J) would masquerade as broken.
    if (!isShellAvailable) return;
    setTerminalOpen((prev) => {
      const next = !prev;
      if (next) {
        requestAnimationFrame(() =>
          window.dispatchEvent(new CustomEvent("terminal:focus-input")),
        );
      }
      return next;
    });
    // If closing terminal while in a focus mode, restore window
    if (terminalOpen && shellFocusLevel > 0) {
      await resizeWindowForFocusLevel(shellFocusLevel, 0);
      setShellFocusLevel(0);
    }
  }, [
    isShellAvailable,
    terminalOpen,
    shellFocusLevel,
    resizeWindowForFocusLevel,
  ]);

  // Auto-open terminal when a payload is sent to the shell.
  // If the terminal panel wasn't open (listener not mounted), buffer
  // the payload and re-dispatch once the panel mounts.
  const pendingPayloadRef = useRef<unknown>(null);
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent).detail;
      setTerminalOpen((prev) => {
        if (!prev) {
          // Panel not mounted yet — buffer for re-dispatch
          pendingPayloadRef.current = detail;
        }
        return true;
      });
    };
    window.addEventListener("message:send-to-shell", handler);
    return () => window.removeEventListener("message:send-to-shell", handler);
  }, []);

  // Flush buffered payload once the terminal panel mounts
  useEffect(() => {
    if (terminalOpen && pendingPayloadRef.current) {
      const detail = pendingPayloadRef.current;
      pendingPayloadRef.current = null;
      // Small delay to let the Shell panel mount and register its listener
      const timer = setTimeout(() => {
        window.dispatchEvent(
          new CustomEvent("message:send-to-shell", { detail }),
        );
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [terminalOpen]);

  useEffect(() => {
    try {
      if (terminalOpen) {
        localStorage.setItem(TERMINAL_OPEN_STORAGE_KEY, "1");
      } else {
        localStorage.removeItem(TERMINAL_OPEN_STORAGE_KEY);
      }
    } catch {
      // Ignore storage failures in restricted contexts.
    }
  }, [terminalOpen]);

  // Cmd+J cycles through 4 view modes:
  // Mode 0: sidebar + chat (no terminal)
  // Mode 1: sidebar + chat + terminal
  // Mode 2: sidebar + terminal (no chat)
  // Mode 3: terminal only
  const handleCycleViewMode = useCallback(async () => {
    if (!terminalOpen) {
      // Mode 0 → Mode 1: open terminal
      setTerminalOpen(true);
      requestAnimationFrame(() =>
        window.dispatchEvent(new CustomEvent("terminal:focus-input")),
      );
    } else if (shellFocusLevel === 0) {
      // Mode 1 → Mode 2: hide chat
      await resizeWindowForFocusLevel(0, 1);
      setShellFocusLevel(1);
    } else if (shellFocusLevel === 1) {
      // Mode 2 → Mode 3: hide sidebar too
      await resizeWindowForFocusLevel(1, 2);
      setShellFocusLevel(2);
    } else {
      // Mode 3 → Mode 0: close terminal, restore window
      await resizeWindowForFocusLevel(2, 0);
      setShellFocusLevel(0);
      setTerminalOpen(false);
    }
  }, [terminalOpen, shellFocusLevel, resizeWindowForFocusLevel]);

  // Coworker admin flow: collapse the Shell panel when opening coworker canvas.
  useEffect(() => {
    const handler = () => setTerminalOpen(false);
    window.addEventListener("ano:collapse-terminal", handler);
    return () => window.removeEventListener("ano:collapse-terminal", handler);
  }, [setTerminalOpen]);

  // Open terminal when a suggestion dispatches terminal:open
  useEffect(() => {
    const handler = () => {
      setTerminalOpen(true);
    };
    window.addEventListener("terminal:open", handler);
    return () => window.removeEventListener("terminal:open", handler);
  }, []);

  // Deep-link click in xterm needs the chat view visible alongside the
  // terminal. Listener flips focus level to 0 (sidebar + chat + terminal)
  // without touching `terminalOpen` — the terminal stays mounted.
  useEffect(() => {
    const handler = () => {
      setShellFocusLevel((prev) => {
        if (prev === 0) return prev;
        void resizeWindowForFocusLevel(prev, 0);
        return 0;
      });
    };
    window.addEventListener("ano:focus-chat-side-by-side", handler);
    return () =>
      window.removeEventListener("ano:focus-chat-side-by-side", handler);
  }, [resizeWindowForFocusLevel]);

  return {
    terminalOpen,
    hasOpenedTerminal,
    shellFocusLevel,
    terminalWidth,
    setTerminalWidth,
    showChat,
    showSidebar,
    handleToggleTerminal,
    handleCycleViewMode,
  };
}
